import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, Loader, RefreshCw } from 'lucide-react';
import { JobType, type WorkflowJob } from '../graphql/types';
import * as jobTypeConfigService from '../services/jobTypeConfigService';
import { WorkspaceRoute } from './WorkspaceRoute';
import { useWorkflowJob } from './useWorkflowJob';
import { OrderRail } from './OrderRail';
import { StepList } from './StepList';
import { RailSlotContext } from './railSlot';
import { getDefaultMode, getLockedModes, readJobWorkspaceParams } from './rowMode';
import {
  getTitleSearchRailSteps,
  getTitleSearchStatusPill,
  getTitleSearchTitle,
} from './titleSearchStatus';
import {
  SELECTION_PARAM_KEYS,
  writeWorkspaceParams,
  type SelectionParamKey,
  type WorkspaceMode,
} from './urlState';
import type { WorkspaceSelection } from './WorkflowWorkspace';

export interface TitleSearchWorkspaceContextValue {
  job: WorkflowJob;
  /** The Mode being shown (URL `mode`, never a locked Mode). */
  mode: WorkspaceMode;
  /** Re-fetch the job; resolves when the load has finished. */
  reload: () => Promise<void>;
  /** Merge a fresher copy of the job from a child component. */
  updateJob: (job: WorkflowJob) => void;
  selection: WorkspaceSelection;
  /** Change selection keys only; the Mode and unrelated params are kept. */
  select: (update: Partial<Record<SelectionParamKey, string | null>>) => void;
  /** Open a Mode (writes `mode` to the URL; selection params are kept). */
  setMode: (mode: WorkspaceMode) => void;
}

const TitleSearchWorkspaceContext = createContext<TitleSearchWorkspaceContextValue | null>(null);

/** Job, loader actions, Mode and selection for the Mode bodies of a TR Workspace. */
export function useTitleSearchWorkspace(): TitleSearchWorkspaceContextValue {
  const ctx = useContext(TitleSearchWorkspaceContext);
  if (!ctx) {
    throw new Error('useTitleSearchWorkspace must be used inside <TitleSearchWorkspace>');
  }
  return ctx;
}

export interface TitleSearchWorkspaceProps {
  jobId: string;
  /** Mode body; reads the job through `useTitleSearchWorkspace()`. */
  children: React.ReactNode;
}

/**
 * Title Search Workspace container. Owns job loading, the
 * `onJobStatusChanged` subscription and the polling fallback
 * (`useWorkflowJob`, merging pushed updates as the legacy page did), feeds the
 * Top_Bar the real title and Status_Pill text, locks Findings and Title
 * analysis until the review is finalized (Review_Gate), puts the OrderRail and
 * StepList in the Rail, and hands the job, Mode and URL selection to the body
 * through context.
 *
 * Requirements: 3.1, 3.5, 3.6, 8.10.
 */
export const TitleSearchWorkspace: React.FC<TitleSearchWorkspaceProps> = ({
  jobId,
  children,
}) => {
  const { job, loading, error, reload, updateJob } = useWorkflowJob(jobId, JobType.TR, {
    mergeUpdates: true,
    reconcileOnReconnect: true,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const config = jobTypeConfigService.getConfig(JobType.TR);
  // End of the Rail, where Review mode lists its documents.
  const [railSlot, setRailSlot] = useState<HTMLElement | null>(null);

  const params = useMemo(
    () => readJobWorkspaceParams(searchParams, JobType.TR, job, { isDraft: false }),
    [searchParams, job]
  );
  const mode = params.mode;

  const locked = useMemo(
    () => (job ? getLockedModes(JobType.TR, job) : undefined),
    [job]
  );

  const selection = useMemo<WorkspaceSelection>(() => {
    const result: WorkspaceSelection = {};
    for (const key of SELECTION_PARAM_KEYS) {
      if (params[key]) result[key] = params[key];
    }
    return result;
  }, [params]);

  const select = useCallback(
    (update: Partial<Record<SelectionParamKey, string | null>>) => {
      setSearchParams(writeWorkspaceParams(searchParams, update));
    },
    [searchParams, setSearchParams]
  );

  const changeMode = useCallback(
    (next: WorkspaceMode) => {
      setSearchParams(writeWorkspaceParams(searchParams, { mode: next }));
    },
    [searchParams, setSearchParams]
  );

  const status = job ? getTitleSearchStatusPill(job) : undefined;

  const contextValue = useMemo<TitleSearchWorkspaceContextValue | null>(
    () => (job ? { job, mode, reload, updateJob, selection, select, setMode: changeMode } : null),
    [job, mode, reload, updateJob, selection, select, changeMode]
  );

  // In Review mode the Rail holds only the received documents (Review mode
  // puts them in the slot). The order summary and step list show in the other
  // Modes; the Top_Bar's Mode_Switch still moves between Modes.
  const rail = job ? (
    <>
      {mode === 'review' ? null : (
        <>
          <OrderRail job={job} />
          <StepList
            steps={getTitleSearchRailSteps(job)}
            activeMode={mode}
            locked={locked}
            onSelectMode={changeMode}
          />
        </>
      )}
      <div ref={setRailSlot} data-testid="rail-slot" />
    </>
  ) : undefined;

  let body: React.ReactNode;
  if (error) {
    body = (
      <div className="p-6">
        <div role="alert" className="max-w-3xl rounded-xl border border-red-200 bg-red-50 p-6">
          <div className="flex items-start">
            <AlertCircle className="h-6 w-6 flex-shrink-0 text-red-500" aria-hidden="true" />
            <div className="ml-4 flex-1">
              <h2 className="text-lg font-medium text-red-800">Error loading job</h2>
              <p className="mt-2 text-red-700">{error}</p>
              <button
                type="button"
                onClick={() => void reload()}
                className="mt-4 inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (!contextValue) {
    // Spinner only while there is no job. Reloads after a step completes keep
    // the current body mounted so its local state survives.
    body = loading ? (
      <div className="flex min-h-[400px] flex-col items-center justify-center">
        <Loader className="mb-4 h-12 w-12 animate-spin text-[#834DFB]" aria-hidden="true" />
        <p className="text-gray-600">Loading job data...</p>
      </div>
    ) : (
      <div className="py-12 text-center">
        <p className="text-gray-600">Job not found.</p>
      </div>
    );
  } else {
    body = (
      <TitleSearchWorkspaceContext.Provider value={contextValue}>
        <RailSlotContext.Provider value={railSlot}>
          <div className="p-6">{children}</div>
        </RailSlotContext.Provider>
      </TitleSearchWorkspaceContext.Provider>
    );
  }

  return (
    <WorkspaceRoute
      feature={JobType.TR}
      itemId={jobId}
      title={getTitleSearchTitle(job, config.displayName)}
      statusLabel={status?.label}
      statusTone={status?.tone}
      defaultMode={job ? getDefaultMode(job) : undefined}
      locked={locked}
      rail={rail}
      railLabel="Title search order"
    >
      {body}
    </WorkspaceRoute>
  );
};

export default TitleSearchWorkspace;
